import {
  Alert,
  Button,
  DataTable,
  type DataTableColumnDef,
  Divider,
  Group,
  Input,
  Select,
  Skeleton,
  Stack,
  Text,
  Tile,
} from "@assetwatch/ui";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { mlApiErrorMessage, mlApiErrorStatus } from "../../api/mlApi";
import type { MpEvent, MpEventsCost } from "./api";
import {
  clampDaysBack,
  DEFAULT_EVENT_DAYS_BACK,
  eventTypeLabel,
  formatEventMetadata,
  graphSearchForEvent,
  isMonitoringPointId,
  sanitizeSearch,
} from "./lib";
import { type MpEventsFilters, useMpEvents, useMpEventTypes } from "./queries";

const DEFAULT_ROWS_PER_PAGE = 25;

function formatTimestamp(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString();
}

/**
 * What actually went wrong.
 *
 * The endpoint's own `{"error": "..."}` message first, then the status-specific
 * hint, then the raw error — a request that returned 200 and failed validation
 * has none of the first two, and claiming "403" for it sends you looking in the
 * wrong place.
 */
function errorSubtext(error: unknown): string {
  const apiMessage = mlApiErrorMessage(error);
  if (apiMessage) {
    return apiMessage;
  }

  if (mlApiErrorStatus(error) === 403) {
    return "Your Cognito identity isn't on the ML API's tester allowlist yet.";
  }

  return error instanceof Error
    ? error.message
    : "The response didn't match the expected shape.";
}

// Surfaced rather than logged for the same reason as on MP Graphs: each search
// scans the lake, and widening the window multiplies the scan invisibly. It's an
// estimate of data scanned, not a bill.
function QueryCostSummary({ cost }: { cost: MpEventsCost | null }) {
  if (!cost) {
    return null;
  }

  const parts = [
    // Nullable means the backend couldn't determine the figure. "$0" would claim
    // the query was free; "unavailable" is the honest rendering.
    cost.usd === null || cost.usd === undefined
      ? "cost unavailable"
      : cost.usd > 0 && cost.usd < 0.0001
        ? "<$0.0001"
        : `$${cost.usd.toFixed(4)}`,
    cost.mb_scanned === null || cost.mb_scanned === undefined
      ? "MB unavailable"
      : `${cost.mb_scanned.toLocaleString(undefined, {
          maximumFractionDigits: 1,
        })} MB scanned`,
    cost.seconds === null || cost.seconds === undefined
      ? null
      : `${cost.seconds.toFixed(2)}s`,
  ].filter((part): part is string => part !== null);

  return (
    <Text size="sm" tone="subtle">
      <span title="Estimated cost of the data scanned by this query. Excludes compute; not a bill.">
        Scan estimate: {parts.join(" · ")}
      </span>
    </Text>
  );
}

const columns: DataTableColumnDef<MpEvent>[] = [
  {
    id: "external_monitoringpoint_id",
    header: "Monitoring Point",
    accessorKey: "external_monitoringpoint_id",
    size: 300,
  },
  {
    id: "sensor",
    header: "Sensor",
    accessorKey: "sensor",
    cell: (info) => (info.getValue() as string | null) ?? "—",
    size: 140,
  },
  {
    id: "event_type",
    header: "Event Type",
    accessorKey: "event_type",
    // The API sends slugs; the table shows the human name for the ones we know
    // and a readable form of the slug for any source added since.
    cell: (info) => eventTypeLabel(info.getValue() as string),
    size: 200,
  },
  {
    id: "timestamp",
    header: "Timestamp",
    accessorKey: "timestamp",
    cell: (info) => formatTimestamp(info.getValue() as number),
    size: 180,
  },
  {
    id: "metadata",
    header: "Metadata",
    accessorKey: "metadata",
    cell: (info) => (
      <span className="block max-w-96 truncate">
        {formatEventMetadata(
          info.getValue() as Parameters<typeof formatEventMetadata>[0],
        )}
      </span>
    ),
    size: 320,
  },
  {
    id: "actions",
    header: "Actions",
    cell: (info) => (
      <Link search={graphSearchForEvent(info.row.original)} to="/mp-graphs">
        View graphs
      </Link>
    ),
    size: 130,
  },
];

export function MpEventsScreen() {
  const [search, setSearch] = useState("");
  const [eventType, setEventType] = useState("");
  const [daysBack, setDaysBack] = useState(String(DEFAULT_EVENT_DAYS_BACK));

  // Filters apply on "View Events", never on keystroke: every application is an
  // Athena scan, so nothing fetches until it's asked for.
  const [applied, setApplied] = useState<MpEventsFilters>();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const eventTypes = useMpEventTypes();
  const eventsQuery = useMpEvents(applied);

  const eventTypeOptions = [
    { value: "", label: "All event types" },
    ...(eventTypes.data ?? []).map((type) => ({
      value: type,
      label: eventTypeLabel(type),
    })),
  ];

  const events = eventsQuery.data?.events ?? [];
  const totalPages = Math.max(1, Math.ceil(events.length / rowsPerPage));
  const currentPage = Math.min(page, totalPages);

  function handleViewEvents() {
    const term = sanitizeSearch(search);
    const days = clampDaysBack(daysBack);

    // Reflect what was actually sent: the endpoint rejects 0 and caps at 365, so
    // the field shouldn't keep claiming a window we didn't query.
    setDaysBack(String(days));
    setSearch(term);

    setApplied({
      daysBack: days,
      eventType: eventType || undefined,
      // A whole GUID goes to the exact-id filter instead of the substring
      // search, which is both cheaper and immune to `search` being applied
      // after each source's row cap.
      ...(isMonitoringPointId(term)
        ? { monitoringPointId: term }
        : term
          ? { search: term }
          : {}),
    });
    setPage(1);
  }

  return (
    <Stack gap={24}>
      <Tile padding="comfortable" radius="md" tone="muted">
        <Group align="end" gap={16} wrap="wrap">
          <div className="w-72">
            <Input
              label="Search"
              name="search"
              onChange={(event) =>
                setSearch(sanitizeSearch(event.target.value))
              }
              placeholder="Monitoring point ID or sensor serial"
              reserveSubtextSpace={false}
              value={search}
            />
          </div>

          <div className="w-56">
            <Select
              disabled={eventTypes.isPending}
              searchable
              label="Event Type"
              onChange={(value) => setEventType(value ?? "")}
              options={eventTypeOptions}
              placeholder={
                eventTypes.isError
                  ? "Event types unavailable"
                  : "All event types"
              }
              reserveSubtextSpace={false}
              value={eventType}
            />
          </div>

          <div className="w-28">
            <Input
              inputMode="numeric"
              label="Days Back"
              name="daysBack"
              onChange={(event) => setDaysBack(event.target.value)}
              reserveSubtextSpace={false}
              value={daysBack}
            />
          </div>

          <Button onClick={handleViewEvents} type="button">
            View Events
          </Button>
        </Group>
      </Tile>

      <Divider emphasis="high" />

      {!applied ? (
        <Text tone="subtle">
          Choose a window, then select “View Events”. Each search scans the data
          lake, so nothing is queried until you ask for it.
        </Text>
      ) : eventsQuery.isError ? (
        <Alert
          subtext={errorSubtext(eventsQuery.error)}
          title="Couldn't load events"
          tone="negative"
        />
      ) : eventsQuery.isPending ? (
        <Skeleton height={320} />
      ) : (
        <Stack gap={16}>
          <Group align="center" gap={16} justify="between" wrap="wrap">
            <Text size="sm" tone="subtle">
              {events.length === 1 ? "1 event" : `${events.length} events`}
            </Text>
            <QueryCostSummary cost={eventsQuery.data?.cost ?? null} />
          </Group>

          {eventsQuery.data?.truncated ? (
            <Alert
              subtext="Older events in this window aren't shown. Narrow the window or filter by event type to see them."
              title="Showing the newest 5,000 events"
              tone="warning"
            />
          ) : null}

          {eventsQuery.data && eventsQuery.data.dropped > 0 ? (
            <Alert
              subtext="Their shape didn't match what the endpoint documents — most likely a timestamp or an id the frontend couldn't read. Worth reporting."
              title={`${eventsQuery.data.dropped} event${
                eventsQuery.data.dropped === 1 ? "" : "s"
              } couldn't be read and ${
                eventsQuery.data.dropped === 1 ? "was" : "were"
              } skipped`}
              tone="warning"
            />
          ) : null}

          <DataTable
            ariaLabel="Measurement point events"
            columns={columns}
            data={events}
            emptyMessage="No events match these filters."
            getRowId={(row, index) =>
              `${row.external_monitoringpoint_id}-${row.timestamp}-${index}`
            }
            pagination={{
              currentPage,
              onPageChange: setPage,
              onRowsPerPageChange: (next) => {
                setRowsPerPage(next);
                setPage(1);
              },
              rowsPerPage,
              totalItems: events.length,
              totalPages,
              variant: "full",
            }}
            size="sm"
          />
        </Stack>
      )}
    </Stack>
  );
}
